import { React, useState, useEffect, useNavigate, Dayjs, dayjs } from '../../utils/globalImports';
import { AppLayout, FilterCard, CustomConfirmDialog, validationMessages, useApi, CustomDataTable, CustomBackdrop, Toaster } from '../../utils/globalImports';
import { ApiResponse } from '../../services/apiService';

interface Framework {
    frameworkId: number;
    frameworkName: string;
    clientId: number;
    clientName: string;
    status: boolean;
    startDate: Dayjs | null;
    endDate: Dayjs | null;
    projectNames: string;
}

const Frameworks = () => {
    const navigate = useNavigate();

    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const [selectedFramework, setSelectedFramework] = useState<any | null>(null);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [frameworkData, setFrameworkData] = useState<any[]>([]);
    const [currentStatus, setcurrentStatus] = useState<string[]>(['Active']);
    const [dialogOpen, setDialogOpen] = useState(false); // State to manage confirm dialog "open/close" state
    const [isOn, setIsOn] = useState<boolean>();  // State to manage the toggle button's "on/off" state
    const [copyDialogOpen, setCopyDialogOpen] = useState(false); // State to manage confirm dialog "open/close" state
    const props = { showframeworkField: true, title: 'Frameworks', actionText: 'Add Framework', searchbarText: validationMessages.framework_searchText }
    const { execute, loading } = useApi();
    const [warnMessage, setWarnMessage] = useState<string>('');
    const [isWarn, setIsWarn] = useState<boolean>(false);

    const userId = Number(localStorage.getItem("userId"));

    const sortOptions = [
        { value: "Framework Name", text: "Framework Name" },
        { value: "Associated Clients", text: "Associated clients" },
    ];

    const handleNavigate = (frameworkId?: number) => {
        if (frameworkId) navigate(`/framework/add-framework/${frameworkId}`);
        else navigate('/framework/add-framework');
    }

    useEffect(() => {
        fetchFrameworks();
    }, []);  // The empty dependency array ensures it only runs once when the component mounts

    // Fetch frameworks
    const fetchFrameworks = async () => {
        const { data } = await execute<Framework[]>('get', '/Framework/GetAllFrameworks');
        if (data) {
            const frameworkData = data
                .map((framework) => {
                    const { frameworkId, frameworkName, clientName, projectNames, endDate, status } = framework;
                    const Status = status ? "Active" : "Inactive";
                    // const shortName = frameworkName
                    //     .split(" ")
                    //     .map((word) => word.charAt(0))
                    //     .join("")
                    //     .toUpperCase();

                    return { frameworkId, frameworkName, clientName, projectNames, endDate, Status };
                });

            setFrameworkData(frameworkData);
            showGrid(frameworkData.filter((framework) => currentStatus.includes(framework.Status)))
        }
    };

    const showGrid = (dataList: any[]) => {
        const frameworks = dataList
            .map((item) => {
                const { frameworkId, shortName, frameworkName, clientName, projectNames, endDate, Status } = item;
                return { frameworkId, "": "", "Framework Name": frameworkName.trim(), "Associated Clients": clientName, "End Date": dayjs(endDate).format("DD/MM/YYYY"), Status };
            })
            .sort((a, b) =>
                a["Framework Name"].localeCompare(b["Framework Name"], undefined, { sensitivity: 'base' }) ||
                a["Associated Clients"].localeCompare(b["Associated Clients"], undefined, { sensitivity: 'base' })
            );

        setFilteredData(frameworks);
    }

    // filter Data
    const handleSearchframeworkData = (query: string, status: Number, category?: number, region?: number, contactType?: number, date?: string) => {
        const lowerCaseQuery = query.toLowerCase();

        // Map status strings to corresponding numbers
        const statusMapping: Record<string, number> = {
            Active: 1,
            Inactive: 0,
        };

        const filtered = frameworkData.filter((item) => {
            // Convert item.Status to its numeric equivalent or skip if it doesn't match
            const itemStatus = statusMapping[item.Status] ?? -1;

            const matchesQuery =
                item.frameworkName?.toLowerCase().includes(lowerCaseQuery) ||
                item.clientName?.toLowerCase().includes(lowerCaseQuery) ||
                (item.projectNames
                    ? item.projectNames
                        .split(",") // Split into an array
                        .map((name: string) => name.trim().toLowerCase()) // Trim and lowercase each name
                        .some((project: string) => project.includes(lowerCaseQuery)) // Check if any match
                    : false); // Return false if projectName is null or undefined

            const matchDate =
                date ? (
                    dayjs(item.endDate).isSame(dayjs(date), "day")
                ) : true;

            const matchesStatus =
                status === -1 || itemStatus === status;

            return matchesQuery && matchDate && matchesStatus;
        });

        setcurrentStatus(
            status === 0
                ? ['Inactive'] // When status is 0, set currentStatus to 'Inactive'
                : status === -1
                    ? ['Active', 'Inactive'] // When status is -1 or 2, set currentStatus to both 'Active' and 'Inactive'
                    : status === 1
                        ? ['Active'] // When status is 1, set currentStatus to 'Active'
                        : currentStatus // Fallback to the current value if no conditions are met
        );
        showGrid(filtered)
    }

    const handleToggleState = (row: any) => {
        if (row.Status === 'Inactive') {
            setIsOn(false)
        } else {
            setIsOn(true)
        }
        setSelectedFramework(row);
        setDialogOpen(true);
        setWarnMessage('')
    }

    const handleEdit = (framework: any) => {
        handleNavigate(framework.frameworkId)
    };

    const handleDialogCancel = () => {
        setDialogOpen(false);
        setIsWarn(false)
        setSelectedFramework(null); // Clear selected row
    }

    const handleDialogConfirm = async () => {
        if (!selectedFramework) return;

        try {
            // Make API call to update the status
            const { data } = await execute<Framework>('get', `/Framework/GetFramework/${selectedFramework.frameworkId}`);
            if (data) {

                if (selectedFramework.Status === 'Active' && data.projectNames && !isWarn) {
                    const warningMessage = validationMessages.framework_dialog_Warning(data.projectNames)
                    setWarnMessage(warningMessage)
                    setIsWarn(true)
                    return;
                    // setToast({ open: true, message: warningMessage, type: "error" });
                }

                // Step 2: Update the fields on the filtered record
                const updatedFrameworkData = {
                    ...data, // Keep the original user data
                    status: selectedFramework.Status === 'Active' ? false : true,
                    updatedId: userId, // You can also update any additional fields like updatedId
                };

                let response: ApiResponse<any> = await execute('put', '/Framework/UpdateFramework', updatedFrameworkData);

                if (response!) {
                    if (response.error) {
                        // If there's an error in the response, display the error message
                        setToast({ open: true, message: response.error, type: "error" });
                    } else {
                        // If the response is successful, display the success message
                        setToast({ open: true, message: response.data, type: "success" });
                    }
                }
                setDialogOpen(false);
                setIsWarn(false)
                fetchFrameworks();
            }

            setSelectedFramework(null); // Clear selected row
        } catch (error) {
            console.error('Error updating status:', error);
        }
        // finally {
        //     setDialogOpen(false);
        // }
    };

    const handleCopy = (row: any) => {
        setCopyDialogOpen(true)
        setSelectedFramework(row);
    }

    const handleConfirmCopy = async () => {
        try {
            const response: ApiResponse<any> = await execute('post', `/Framework/clone/${selectedFramework.frameworkId}/${userId}`);
            if (response!) {
                if (response.error) {
                    // If there's an error in the response, display the error message
                    setToast({ open: true, message: validationMessages.common_Error, type: "error" });
                } else {
                    // If the response is successful, display the success message
                    setToast({ open: true, message: validationMessages.common_CopySucess("Framework"), type: "success" });
                    fetchFrameworks();
                }
            }
            setSelectedFramework(null); // Clear selected row
        }
        finally {
            setCopyDialogOpen(false)
        }
    }

    return (
        <AppLayout>
            <FilterCard  {...props} handleModalOrNavigate={() => handleNavigate()} handleSearch={handleSearchframeworkData} ></FilterCard>
            {frameworkData.length > 0 && (
                <CustomDataTable
                    rows={filteredData}
                    actionText="Framework"
                    sortOptions={sortOptions}
                    onRowEdit={handleEdit}
                    onRowDoubleClick={handleEdit}
                    onRowCopy={handleCopy}
                    onToggleClick={handleToggleState}
                    isDialogOpen={dialogOpen}
                    isToggleOn={isOn}
                    handleCancel={handleDialogCancel}
                    handleConfirm={handleDialogConfirm}
                    noResultMessage={validationMessages.common_RecordNotFound("frameworks")}
                    frameworkMessage={warnMessage}
                >
                </CustomDataTable>)}


            <CustomBackdrop open={loading} />
            <Toaster open={toast.open} message={toast.message} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />
            <CustomConfirmDialog
                open={copyDialogOpen}
                title={validationMessages.common_CopyTitle}
                message={validationMessages.common_CopyMessage("framework")}
                onConfirm={handleConfirmCopy}
                onCancel={() => {
                    setCopyDialogOpen(false)
                }}
                onClose={() => {
                    setCopyDialogOpen(false)
                }}
            />
        </AppLayout>
    );
}

export default Frameworks;